package com.flowyride.feature.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.domain.account.AccountException
import com.flowyride.core.domain.account.AccountRepository
import com.flowyride.core.domain.account.AccountStatus
import com.flowyride.core.domain.account.AdminRepository
import com.flowyride.core.domain.account.ManagedUser
import com.flowyride.core.domain.account.SessionState
import com.flowyride.core.domain.account.UserRole
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.error_forbidden
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource

data class TemporaryPassword(val username: String, val password: String)

data class AdminUiState(
    val users: List<ManagedUser> = emptyList(),
    val currentUserId: Long? = null,
    val loading: Boolean = true,
    val error: StringResource? = null,
    /** Shown once, until dismissed. */
    val temporaryPassword: TemporaryPassword? = null,
) {
    val pending get() = users.filter { it.user.status == AccountStatus.PENDING }
    val active get() = users.filter { it.user.status == AccountStatus.APPROVED }
    val inactive get() = users.filter { it.user.status == AccountStatus.REJECTED || it.user.status == AccountStatus.REVOKED }
}

sealed interface AdminAction {
    data object Refresh : AdminAction
    data class Approve(val userId: Long) : AdminAction
    data class Reject(val userId: Long) : AdminAction
    data class Revoke(val userId: Long) : AdminAction
    data class SetRole(val userId: Long, val role: UserRole) : AdminAction
    data class ResetPassword(val userId: Long) : AdminAction
    data object TemporaryPasswordDismissed : AdminAction
}

class AdminViewModel(
    private val admin: AdminRepository,
    accounts: AccountRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(
        AdminUiState(currentUserId = (accounts.session.value as? SessionState.SignedIn)?.user?.id),
    )
    val state: StateFlow<AdminUiState> = _state.asStateFlow()

    init {
        refresh()
    }

    fun onAction(action: AdminAction) {
        when (action) {
            AdminAction.Refresh -> refresh()
            is AdminAction.Approve -> act { admin.approve(action.userId) }
            is AdminAction.Reject -> act { admin.reject(action.userId) }
            is AdminAction.Revoke -> act { admin.revoke(action.userId) }
            is AdminAction.SetRole -> act { admin.setRole(action.userId, action.role) }
            is AdminAction.ResetPassword -> act {
                val password = admin.resetPassword(action.userId)
                val username = _state.value.users.firstOrNull { it.user.id == action.userId }?.user?.username.orEmpty()
                _state.update { it.copy(temporaryPassword = TemporaryPassword(username, password)) }
            }
            AdminAction.TemporaryPasswordDismissed -> _state.update { it.copy(temporaryPassword = null) }
        }
    }

    private fun refresh() = act { }

    /** Runs [block], then reloads the list: the server is the source of truth. */
    private fun act(block: suspend () -> Unit) {
        _state.update { it.copy(loading = true, error = null) }
        viewModelScope.launch {
            val error = try {
                block()
                null
            } catch (e: AccountException) {
                e.toAdminMessage()
            }
            try {
                val users = admin.users()
                _state.update { it.copy(users = users, loading = false, error = error) }
            } catch (e: AccountException) {
                _state.update { it.copy(loading = false, error = error ?: e.toAdminMessage()) }
            }
        }
    }

    private fun AccountException.toAdminMessage() = if (this is AccountException.Invalid) Res.string.error_forbidden else toMessage()
}
